// imports
import { FC, useCallback, useMemo, useState } from 'react';
// components
import InfiniteSelect from './InfiniteSelect';
// interfaces, constants, helper
import { HTTP_STATUS } from 'constants/index';
import { BottleSizeSelectProps, SelectType } from 'interfaces';
import { BottleSizesPayload, useFindAllBottleSizeSelectorLazyQuery } from 'generated/graphql';

const BottleSizeSelect: FC<BottleSizeSelectProps> = ({
  name,
  title,
  value,
  isActive,
  handleChange,
  ...rest
}) => {
  const [totalPages, setTotalPages] = useState<number>(0);
  const [getLoading, setGetLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<BottleSizesPayload['data']>([]);

  const updatedOptions = useMemo(() => {
    if (options?.length) {
      return options?.map<SelectType>((item) => {
        const { id, name, isActive } = item || {};

        return {
          value: id,
          name: name || '',
          isActive: isActive || false,
        };
      });
    }
    return [];
  }, [options]);

  const [findAllBottleSize, { loading }] = useFindAllBottleSizeSelectorLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllBottleSize } = data;
      const { data: bottleSizes, response, pagination } = findAllBottleSize || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { totalPages: pages } = pagination || {};

        setTotalPages(pages);
        setOptions([
          ...(getLoading ? [] : options || []),
          ...((bottleSizes as BottleSizesPayload['data']) ?? []),
        ]);
        setGetLoading(false);
      }
    },
    onError: () => {
      setOptions([]);
      setGetLoading(false);
    },
  });

  const fetchData = useCallback(
    async (page?: number, query?: string) => {
      if (!page) setGetLoading(true);
      await findAllBottleSize({
        variables: {
          findAllBottleSizesInput: {
            ...(query && { search: query }),
            paginationOptions: { page: page || 1, limit: 10 },
            ...(typeof isActive === 'boolean' && { isActive }),
          },
        },
      });
    },
    [findAllBottleSize, isActive],
  );

  return (
    <InfiniteSelect
      name={name}
      title={title}
      value={value}
      loading={loading}
      fetchData={fetchData}
      setOptions={setOptions}
      totalPages={totalPages}
      getLoading={getLoading}
      options={updatedOptions}
      handleChange={handleChange}
      {...rest}
    />
  );
};

export default BottleSizeSelect;
